import { createFileRoute } from '@tanstack/react-router';
import { UpdatePasswordForm } from '@/components/features/auth/UpdatePasswordForm';
import { UpdateWebhookForm } from '@/components/features/auth/UpdateWebhookForm';
import { TwoFactorManagement } from '@/components/features/auth/TwoFactorManagement';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CopyButton } from '@/components/shared/CopyButton';
import { PageHeader } from '@/components/shared/PageHeader';
import { User, Mail, Shield, Key, Calendar } from 'lucide-react';
import { formatDate } from '@/utils/formatters';
import { maskApiKey } from '@/utils/crypto';
import { useUser } from '@/lib/hooks';

const ProfilePage = () => {
  const { data: user } = useUser();

  if (!user) return null;

  return (
    <div className="space-y-6">
      <PageHeader title="Профиль" description="Управление настройками аккаунта" />

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Информация об аккаунте</CardTitle>
            <CardDescription>Ваши личные данные</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium">Имя</p>
                <p className="text-sm text-muted-foreground">{user.name}</p>
              </div>
            </div>

            <Separator />

            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium">Email</p>
                <p className="text-sm text-muted-foreground">{user.email}</p>
              </div>
            </div>

            <Separator />

            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium">Роль</p>
                <div>
                  <Badge variant={user.role === 'admin' ? 'default' : 'outline'}>
                    {user.role === 'admin' ? 'Администратор' : 'Клиент'}
                  </Badge>
                </div>
              </div>
            </div>

            <Separator />

            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium">Дата регистрации</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(user.created_at, 'short')}
                </p>
              </div>
            </div>

            {user.last_login_at && (
              <>
                <Separator />
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium">Последний вход</p>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(user.last_login_at, 'full')}
                    </p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>API учётные данные</CardTitle>
            <CardDescription>Ваш API ключ для интеграции</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <Key className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium">API ключ</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                    {user.api_key ? maskApiKey(user.api_key, 8) : 'Недоступен'}
                  </code>
                  {user.api_key && <CopyButton text={user.api_key} />}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Храните API ключ в безопасности и никогда не публикуйте его
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <TwoFactorManagement user={user} />
        <div className="space-y-6">
          <UpdatePasswordForm />
          <UpdateWebhookForm />
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/_auth/profile/')({
  component: ProfilePage,
});
